// Histórico de sessões (T4): janela suspensa com as sessões dos últimos 7 dias de todas as contas.
//
// Lê GET /api/history ao abrir e a cada HISTORY_MS enquanto a janela está aberta. A busca filtra na
// hora (sem acento e sem maiúscula) por título, projeto ou conta. Cada sessão mostra o selo da conta
// e, se ainda estiver viva no escritório, o selo "aberta". Clicar abre a conversa no terminal ao
// vivo: o agente, se estiver vivo, ou a sessão encerrada (GET /api/sessions/:conta/:sessionId/transcript).
// Só leitura: membro também vê e lê.
//
// A janela liga pelo evento 'agzos:painel' com detail 'historico' (botão da barra de ferramentas, T1).

import type { HistoryResponse, HistorySession } from '../core/history.js';
import type { Agent, Provider } from '../core/types.js';
import type { CliAccount } from '../core/settings.js';
import { localeTag, plural, t } from './i18n.js';
import type { SessaoEncerrada } from './live.js';
import { PROVIDER_COLORS, PROVIDER_TAGS } from './sprites.js';

/** Atualização da lista enquanto a janela está aberta (ms). */
export const HISTORY_MS = 15_000;
const DAY_MS = 86_400_000;

/** Uma sessão da lista, com a conta de onde veio. */
export interface Linha {
  conta: string;
  /** Nome da conta (rótulo, ou o id quando não há rótulo). */
  nome: string;
  provider: Provider;
  s: HistorySession;
}

/** Dia da lista: chave estável (AAAA-MM-DD, dia local) e o rótulo que aparece. */
export interface Dia {
  chave: string;
  rotulo: string;
  linhas: Linha[];
}

const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);

/** Minúsculas e sem acento: 'Sessão' acha 'sessao'. */
export function normaliza(texto: string): string {
  return texto.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
}

/** Sessões que batem com a busca (título, projeto ou conta). Busca vazia devolve tudo. */
export function filtra(linhas: Linha[], busca: string): Linha[] {
  const termo = normaliza(busca);
  if (!termo) return linhas;
  return linhas.filter((l) => normaliza(`${l.s.title} ${l.s.project} ${l.nome}`).includes(termo));
}

/** Achata a resposta de /api/history numa lista só, da mais nova para a mais antiga. */
export function linhasDe(resposta: HistoryResponse): Linha[] {
  return resposta.accounts
    .flatMap((c) => c.sessions.map((s) => ({ conta: c.id, nome: c.label ?? c.id, provider: c.provider, s })))
    .sort((a, b) => b.s.lastEventAt - a.s.lastEventAt);
}

/** Dia local de um instante, como AAAA-MM-DD. */
function diaDe(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Agrupa por dia local, do mais novo para o mais antigo: Hoje, Ontem, depois a data. `agora` entra para os testes. */
export function agrupaPorDia(linhas: Linha[], agora = Date.now()): Dia[] {
  const hoje = new Date(agora);
  const hojeChave = diaDe(agora);
  const ontemChave = diaDe(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 1).getTime());
  const dias = new Map<string, Dia>();
  for (const linha of [...linhas].sort((a, b) => b.s.lastEventAt - a.s.lastEventAt)) {
    const chave = diaDe(linha.s.lastEventAt);
    let dia = dias.get(chave);
    if (!dia) {
      dia = { chave, rotulo: rotuloDo(chave, linha.s.lastEventAt, hojeChave, ontemChave), linhas: [] };
      dias.set(chave, dia);
    }
    dia.linhas.push(linha);
  }
  return [...dias.values()];
}

function rotuloDo(chave: string, ms: number, hoje: string, ontem: string): string {
  if (chave === hoje) return t('historico.hoje');
  if (chave === ontem) return t('historico.ontem');
  const mesmoAno = new Date(ms).getFullYear() === new Date().getFullYear();
  return new Date(ms).toLocaleDateString(localeTag(), { weekday: 'short', day: 'numeric', month: 'long', ...(mesmoAno ? {} : { year: 'numeric' }) });
}

/** Janela do histórico. Os textos saem de t(); a lista se redesenha sozinha a cada leitura. */
export interface HistoryDeps {
  /** Agentes vivos no escritório (selo "aberta" e clique no agente vivo). */
  agentes: () => Agent[];
  /** Contas ligadas (sigla e cor do selo). */
  contas: () => Array<Pick<CliAccount, 'id' | 'tag' | 'color'>>;
  /** Abre o agente vivo no terminal ao vivo. */
  abreVivo: (agentId: string) => void;
  /** Abre a sessão encerrada no terminal ao vivo. */
  abreEncerrada: (sessao: SessaoEncerrada) => void;
}

export class HistoryPanel {
  readonly el: HTMLElement;
  private linhas: Linha[] = [];
  /** O que a lista mostra agora (depois da busca), para o clique achar a linha. */
  private visiveis: Linha[] = [];
  private contasComErro: string[] = [];
  private carregado = false;
  private erroLeitura = false;
  private busca = '';
  private timer: number | undefined;

  constructor(
    host: HTMLElement,
    private readonly deps: HistoryDeps,
  ) {
    this.el = document.createElement('section');
    this.el.className = 'hist';
    this.el.hidden = true;
    this.el.setAttribute('role', 'dialog');
    this.el.setAttribute('aria-label', t('historico.titulo'));
    this.el.innerHTML = `<header class="hist-cab">
        <b class="hist-titulo">${esc(t('historico.titulo'))}</b>
        <button class="hist-fechar" type="button" aria-label="${esc(t('acao.fechar'))}" title="${esc(t('acao.fechar'))}">✕</button>
      </header>
      <p class="hist-explica">${esc(t('historico.explica'))}</p>
      <div class="hist-busca"><input type="search" placeholder="${esc(t('historico.busca'))}" aria-label="${esc(t('historico.busca'))}" autocomplete="off" /></div>
      <div class="hist-corpo" aria-live="polite"></div>
      <p class="hist-rodape"></p>`;
    host.append(this.el);
    this.bind();
  }

  get isOpen(): boolean {
    return !this.el.hidden;
  }

  abre(): void {
    this.el.hidden = false;
    this.render();
    void this.carrega();
    window.clearInterval(this.timer);
    this.timer = window.setInterval(() => void this.carrega(), HISTORY_MS);
    this.q<HTMLInputElement>('input').focus();
  }

  close(): void {
    window.clearInterval(this.timer);
    this.timer = undefined;
    this.el.hidden = true;
  }

  alterna(): void {
    if (this.isOpen) this.close();
    else this.abre();
  }

  // ------------------------------------------------------------------ interno

  private q<T extends HTMLElement = HTMLElement>(sel: string): T {
    return this.el.querySelector(sel) as T;
  }

  private bind(): void {
    this.q('.hist-fechar').addEventListener('click', () => this.close());
    const busca = this.q<HTMLInputElement>('.hist-busca input');
    busca.addEventListener('input', () => {
      this.busca = busca.value;
      this.render();
    });
    busca.addEventListener('keydown', (e) => e.stopPropagation());
    this.q('.hist-corpo').addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-sessao]');
      const linha = this.visiveis.find((l) => l.conta === b?.dataset.conta && l.s.sessionId === b?.dataset.sessao);
      if (linha) this.abreLinha(linha);
    });
    // Esc fecha a janela antes do atalho do escritório (que também limpa a seleção do agente)
    window.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape' || this.el.hidden) return;
        e.stopPropagation();
        this.close();
      },
      true,
    );
  }

  private async carrega(): Promise<void> {
    const res = await fetch('/api/history').catch(() => null);
    const json = (await res?.json().catch(() => undefined)) as HistoryResponse | { ok?: false } | undefined;
    if (json?.ok === true) {
      this.linhas = linhasDe(json);
      this.contasComErro = json.accounts.filter((c) => c.error).map((c) => c.label ?? c.id);
      this.erroLeitura = false;
    } else {
      this.erroLeitura = true;
    }
    this.carregado = true;
    this.render();
  }

  private render(): void {
    const corpo = this.q('.hist-corpo');
    if (!this.carregado) {
      corpo.innerHTML = `<p class="hist-vazio">${esc(t('historico.carregando'))}</p>`;
      return;
    }
    this.visiveis = filtra(this.linhas, this.busca);
    const vivas = new Set(this.deps.agentes().map((a) => `${a.provider}:${a.sessionId}`));
    const dias = agrupaPorDia(this.visiveis);
    const lista = dias.length
      ? dias
          .map((d) => `<section class="hist-dia"><h4>${esc(d.rotulo)}</h4><ul>${d.linhas.map((l) => this.itemHtml(l, vivas.has(`${l.provider}:${l.s.sessionId}`))).join('')}</ul></section>`)
          .join('')
      : `<p class="hist-vazio">${esc(this.busca.trim() ? t('historico.nada') : t('historico.vazio'))}</p>`;
    const avisos = [
      ...(this.erroLeitura ? [t('erro.nao_deu')] : []),
      ...this.contasComErro.map((nome) => `${nome}: ${t('conta.erro_leitura')}`),
    ].map((a) => `<p class="hist-aviso">${esc(a)}</p>`).join('');
    corpo.innerHTML = lista + avisos;
    this.q('.hist-rodape').textContent = plural('historico.sessoes', this.visiveis.length);
  }

  private itemHtml(l: Linha, viva: boolean): string {
    const selo = this.seloDe(l);
    const hora = new Date(l.s.lastEventAt).toLocaleTimeString(localeTag(), { hour: '2-digit', minute: '2-digit' });
    const titulo = l.s.title ? esc(l.s.title) : `<i>${esc(t('historico.sem_titulo'))}</i>`;
    return `<li><button type="button" class="hist-item" data-conta="${esc(l.conta)}" data-sessao="${esc(l.s.sessionId)}">
        <span class="hist-selo" style="background:${esc(selo.cor)}" title="${esc(l.nome)}">${esc(selo.sigla)}</span>
        <span class="hist-texto"><b>${titulo}</b><small>${esc(l.s.project)} · ${esc(hora)}</small></span>
        ${viva ? `<span class="hist-aberta">${esc(t('historico.aberta'))}</span>` : ''}
      </button></li>`;
  }

  /** Sigla e cor da conta (as do CMS; sem elas, as do provider). */
  private seloDe(l: Linha): { sigla: string; cor: string } {
    const conta = this.deps.contas().find((c) => c.id === l.conta);
    return { sigla: conta?.tag || PROVIDER_TAGS[l.provider] || '?', cor: conta?.color || PROVIDER_COLORS[l.provider] || '#888' };
  }

  private abreLinha(l: Linha): void {
    const viva = this.deps.agentes().find((a) => a.provider === l.provider && a.sessionId === l.s.sessionId);
    if (viva) {
      this.deps.abreVivo(viva.id);
      return;
    }
    const selo = this.seloDe(l);
    this.deps.abreEncerrada({
      conta: l.conta,
      sessionId: l.s.sessionId,
      titulo: l.s.title,
      projeto: l.s.project,
      quando: l.s.lastEventAt,
      tag: { label: selo.sigla, color: selo.cor },
    });
  }
}

/** Monta a janela, liga o evento 'agzos:painel' (detail 'historico') e registra o botão na barra. */
export function mountHistory(host: HTMLElement, deps: HistoryDeps): HistoryPanel {
  const painel = new HistoryPanel(host, deps);
  window.addEventListener('agzos:painel', (e) => {
    if ((e as CustomEvent<string>).detail === 'historico') painel.alterna();
  });
  // o botão é da barra (src/web/toolbar.ts), que pede o painel pelo evento 'agzos:painel'
  return painel;
}
